import * as React from 'react';

import { cn } from '@/shared/lib/utils';
import { OverflowTooltip } from './overflow-tooltip.js';

/**
 * ── DIVERGED FROM UPSTREAM shadcn ───────────────────────────────────────────
 *
 * Do not `npx shadcn@latest add table --overwrite` without re-reading this: it
 * would discard the design's table treatment and every table in every portal
 * would quietly revert to generic.
 *
 * The design specifies its own table, and the differences are all deliberate:
 *
 * - Column heads are 10.5px MONO, uppercase, at 0.055em tracking. That reads as
 *   a label rather than as a short sentence, which is what stops a ten-column
 *   header competing with the data underneath it.
 * - Cells are 12.5px at 9px/12px padding - denser than shadcn's default,
 *   because these are triage tables. An admin scans forty rows looking for the
 *   one that is wrong, and comfortable row heights mean less of the estate
 *   fits on screen.
 * - `whitespace-nowrap` on cells, with the container scrolling horizontally.
 *   A wrapped fingerprint or date is unreadable, and a table that grows taller
 *   to avoid a scrollbar is worse than one that scrolls.
 * - Cells truncate. In a `table-fixed` table, a value longer than its column
 *   ends in an ellipsis instead of spilling into the next one, and hovering it
 *   shows the whole value (OverflowTooltip - only when something is actually
 *   cut off). `truncate={false}` opts a cell out: action buttons, anything
 *   that must never be clipped.
 *
 * No zebra striping: rows are plain, distinguished only by the border between
 * them. Tried alternating rows here before - it fought with every chip and
 * pill that shares a background token with the stripe itself, disappearing
 * or half-blending depending on which row it landed on. Plain rows have no
 * such failure mode.
 */

function Table({ className, ...props }: React.ComponentProps<'table'>) {
  return (
    <div data-slot="table-container" className="relative w-full overflow-x-auto">
      <table
        data-slot="table"
        className={cn('w-full caption-bottom border-collapse text-[12.5px]', className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<'thead'>) {
  return (
    <thead
      data-slot="table-header"
      className={cn('bg-muted/40 [&_tr]:border-b [&_tr]:border-border', className)}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<'tbody'>) {
  return (
    <tbody
      data-slot="table-body"
      className={cn('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<'tfoot'>) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn('border-t bg-muted/50 font-medium [&>tr]:last:border-b-0', className)}
      {...props}
    />
  );
}

function TableRow({ className, ...props }: React.ComponentProps<'tr'>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        'border-b border-border transition-colors hover:bg-muted/60 has-aria-expanded:bg-muted/60 data-[state=selected]:bg-muted',
        className,
      )}
      {...props}
    />
  );
}

function TableHead({ className, ...props }: React.ComponentProps<'th'>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        'px-3 py-2.5 text-left align-middle font-mono text-[10.5px] font-semibold tracking-[0.055em] whitespace-nowrap text-muted-foreground uppercase [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        className,
      )}
      {...props}
    />
  );
}

function TableCell({
  className,
  truncate = true,
  ...props
}: React.ComponentProps<'td'> & { truncate?: boolean }) {
  const cell = (
    <td
      data-slot="table-cell"
      className={cn(
        'px-3 py-2.5 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]',
        truncate && 'overflow-hidden text-ellipsis',
        className,
      )}
      {...props}
    />
  );
  return truncate ? <OverflowTooltip>{cell}</OverflowTooltip> : cell;
}

function TableCaption({ className, ...props }: React.ComponentProps<'caption'>) {
  return (
    <caption
      data-slot="table-caption"
      className={cn('mt-4 text-[12.5px] text-muted-foreground', className)}
      {...props}
    />
  );
}

/**
 * The card a table sits in: 16px radius, hairline border, clipped corners.
 *
 * `overflow-hidden` is what makes the corners work - without it the header's
 * tinted band squares off the top two, which is the detail that makes a table
 * look bolted on rather than designed.
 */
function TableCard({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="table-card"
      className={cn(
        'overflow-hidden rounded-2xl border border-border bg-card shadow-[0_1px_3px_rgba(15,23,42,0.05)]',
        className,
      )}
      {...props}
    />
  );
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  TableCard,
};
